import { MessageBar, MessageBarType, Stack } from '@fluentui/react';
import { Breadcrumb } from '@h2oai/ui-kit';

import type { Workspace } from '../../../../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { usePermissions } from '../../../../authz/providers/PermissionsProvider/PermissionsProvider';
import { WorkspacesDropdown } from '../../../../components/Navigation/components/WorkspacesDropdown';
import type { S3Object } from '../../../../drive/types';
import { DriveUserActionType } from '../../../../drive/userActions';
import { DirectoryList } from './DirectoryList';
import { buildBreadcrumbs } from './utils';

interface ModalContentProps {
  currentPath: string;
  objects: S3Object[];
  onNavigate: (path: string) => void;
  selectedWorkspace: Workspace | undefined;
  onWorkspaceChange: (workspace: Workspace | undefined) => void;
  isCrossWorkspace?: boolean;
  operation?: 'copy' | 'move';
  sourceWorkspace?: Workspace;
}

export const ModalContent = (props: ModalContentProps) => {
  const {
    currentPath,
    objects,
    onNavigate,
    selectedWorkspace,
    onWorkspaceChange,
    isCrossWorkspace,
    operation,
    sourceWorkspace,
  } = props;
  const breadcrumbs = buildBreadcrumbs(currentPath, onNavigate);
  const [canWriteObjects] = usePermissions([DriveUserActionType.WRITE_OBJECTS], selectedWorkspace?.name);
  const hasPermissionError = isCrossWorkspace && !canWriteObjects;

  const showCopyWarning = operation === 'copy' && isCrossWorkspace;
  const showMoveWarning = operation === 'move' && isCrossWorkspace;

  return (
    <Stack tokens={{ childrenGap: 16 }}>
      {showCopyWarning && (
        <MessageBar messageBarType={MessageBarType.warning}>
          This item will be copied to &quot;{selectedWorkspace?.displayName || 'the destination workspace'}&quot;.
          Members of &quot;{selectedWorkspace?.displayName}&quot; with Drive Reader role will be able to access it.
        </MessageBar>
      )}

      {showMoveWarning && (
        <MessageBar messageBarType={MessageBarType.warning}>
          Moving this item will remove it from &quot;{sourceWorkspace?.displayName || 'the source workspace'}&quot;.
          Members of &quot;{sourceWorkspace?.displayName}&quot; will lose access to this item, while members of &quot;
          {selectedWorkspace?.displayName}&quot; with Drive Reader role will gain access.
        </MessageBar>
      )}

      <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
        <Stack>
          <WorkspacesDropdown
            onChange={(_e, option) => {
              onWorkspaceChange(option?.data);
            }}
            selectedKey={selectedWorkspace?.name}
          />
        </Stack>
        <Stack styles={{ root: { width: '100%' } }}>
          <Breadcrumb
            items={breadcrumbs.map((crumb) => ({
              text: crumb.text,
              key: crumb.key,
              onClick: crumb.onClick,
            }))}
          />
        </Stack>
      </Stack>

      <Stack
        style={{
          border: '1px solid #e1dfdd',
          borderRadius: '4px',
          overflowY: 'auto',
          backgroundColor: '#fafafa',
          height: 400,
          overflowX: 'auto',
        }}
      >
        <DirectoryList
          objects={objects}
          currentPath={currentPath}
          onNavigate={onNavigate}
          permissionError={hasPermissionError}
        />
      </Stack>
    </Stack>
  );
};
